import { useCallback, useMemo, useRef, useState } from 'react';

import { convertBase64ToBlob } from '../voiceUtils.js';

export interface AudioOutputMessage {
  id: string;
  data: string;
}

export const useSoundPlayer = (props: {
  onError: (message: string) => void;
  onPlayAudio: (id: string) => void;
  onStopAudio: (id: string) => void;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);

  const audioContext = useRef<AudioContext | null>(null);
  const gainNode = useRef<GainNode | null>(null);
  const isInitialized = useRef(false);
  const generation = useRef(0);
  const failedSequences = useRef(new Set<number>());

  /**
   * Decoded clips that cannot be scheduled yet because an EARLIER one has not
   * finished decoding.
   *
   * `addToQueue` is async — it awaits `decodeAudioData` — so two concurrent
   * calls with different-sized buffers can finish in reverse order. The queue
   * used to be built in decode-completion order, which meant the caller could
   * hear the agent's sentences out of sequence, with nothing in the wire
   * protocol able to detect it.
   */
  const pendingClips = useRef<
    Array<{ sequence: number; id: string; buffer: AudioBuffer }>
  >([]);
  const [queueLength, setQueueLength] = useState(0);

  /** Stamped synchronously on arrival — this is the real playback order. */
  const nextSequence = useRef(0);
  const nextExpectedSequence = useRef(0);

  /**
   * Every source that has been handed to the AudioContext and not yet ended,
   * including ones scheduled to start in the future.
   *
   * A barge-in has to be able to stop ALL of them, not just whichever is
   * audible: with look-ahead scheduling several clips are queued on the audio
   * clock at once, and leaving them would keep playing speech the caller has
   * already talked over.
   */
  const scheduledSources = useRef(new Set<AudioBufferSourceNode>());

  /**
   * The AudioContext-clock time the next clip should begin at.
   *
   * This is the fix for the audible gap between clips. Each clip used to start
   * with `bufferSource.start(0)` only once the PREVIOUS clip's `ended` event
   * had made it back to the main thread — so the gap *was* that round trip,
   * and the server's 200 ms aggregation only made it rarer, never absent.
   * Scheduling against the audio clock is sample-accurate and genuinely
   * gapless.
   */
  const nextStartTime = useRef(0);

  const onPlayAudio = useRef<typeof props.onPlayAudio>(props.onPlayAudio);
  onPlayAudio.current = props.onPlayAudio;

  const onStopAudio = useRef<typeof props.onStopAudio>(props.onStopAudio);
  onStopAudio.current = props.onStopAudio;

  const onError = useRef<typeof props.onError>(props.onError);
  onError.current = props.onError;

  // Greeting-latency instrumentation (changelist §17 item 3) — marks the
  // moment decoded audio actually starts on the speakers, so a gap between
  // "chunk received" (VoiceProvider) and this can be attributed to
  // decodeAudioData/queueing here rather than the network or Gemini itself.
  const hasLoggedFirstPlay = useRef(false);

  /**
   * Hands one clip to the audio clock at the running cursor.
   *
   * `onPlayAudio` fires here, at SCHEDULE time, which can be up to one buffer
   * ahead of the clip becoming audible — the Web Audio API exposes no
   * "started" event to hang it on. `isPlaying` stays accurate (and is in fact
   * steadier than before, since it no longer flickers false in the gap between
   * clips), and that is what drives the UI.
   */
  const scheduleClip = useCallback(
    (clip: { id: string; buffer: AudioBuffer }) => {
      const context = audioContext.current;
      const gain = gainNode.current;

      if (!context || !gain) {
        onError.current('Audio environment is not initialized');

        return;
      }

      const source = context.createBufferSource();
      source.buffer = clip.buffer;
      source.connect(gain);

      // Never schedule in the past: after a silence the cursor has fallen
      // behind the clock, and `start()` with a past time plays immediately
      // but would leave the cursor wrong for everything after it.
      const startAt = Math.max(context.currentTime, nextStartTime.current);

      source.start(startAt);
      nextStartTime.current = startAt + clip.buffer.duration;

      scheduledSources.current.add(source);
      setIsPlaying(true);

      if (!hasLoggedFirstPlay.current) {
        hasLoggedFirstPlay.current = true;
        console.log('First decoded audio clip scheduled', clip.id);
      }

      onPlayAudio.current(clip.id);

      source.addEventListener('ended', () => {
        source.disconnect();
        if (!scheduledSources.current.delete(source)) return;
        onStopAudio.current(clip.id);

        if (scheduledSources.current.size === 0) {
          setIsPlaying(false);
        }
      });
    },
    [],
  );

  /**
   * Schedules every decoded clip that is contiguous from the next expected
   * sequence, stopping at the first gap so a late decode cannot jump ahead of
   * an earlier one.
   */
  const drainReadyClips = useCallback(() => {
    pendingClips.current.sort((a, b) => a.sequence - b.sequence);

    while (true) {
      if (failedSequences.current.delete(nextExpectedSequence.current)) {
        nextExpectedSequence.current += 1;
        continue;
      }
      if (pendingClips.current[0]?.sequence !== nextExpectedSequence.current)
        break;
      const clip = pendingClips.current.shift();

      nextExpectedSequence.current += 1;

      if (clip) {
        scheduleClip(clip);
      }
    }

    setQueueLength(pendingClips.current.length);
  }, [scheduleClip]);

  const initPlayer = useCallback(() => {
    if (isInitialized.current) return;
    generation.current += 1;
    nextExpectedSequence.current = nextSequence.current;
    failedSequences.current.clear();
    const initAudioContext = new AudioContext();
    audioContext.current = initAudioContext;

    // Only create a gain node for volume control
    const gain = initAudioContext.createGain();
    gain.connect(initAudioContext.destination);
    gainNode.current = gain;

    isInitialized.current = true;
    hasLoggedFirstPlay.current = false;
    nextStartTime.current = 0;
  }, []);

  const addToQueue = useCallback(
    async (message: AudioOutputMessage) => {
      if (!isInitialized.current || !audioContext.current) {
        onError.current('Audio player has not been initialized');

        return;
      }

      const context = audioContext.current;
      const clipGeneration = generation.current;

      // Claimed synchronously, before the first await — arrival order is the
      // only thing that knows the real playback order.
      const sequence = nextSequence.current;
      nextSequence.current += 1;

      try {
        // The server sends WAV; declaring mp3 worked only because
        // `decodeAudioData` sniffs the bytes, and it actively misled anyone
        // debugging the audio path.
        const blob = convertBase64ToBlob(message.data, 'audio/wav');
        const arrayBuffer = await blob.arrayBuffer();
        if (clipGeneration !== generation.current) return;
        const audioBuffer = await context.decodeAudioData(arrayBuffer);
        if (clipGeneration !== generation.current) return;

        pendingClips.current.push({
          sequence,
          id: message.id,
          buffer: audioBuffer,
        });
        drainReadyClips();
      } catch (e) {
        if (clipGeneration !== generation.current) return;
        failedSequences.current.add(sequence);
        drainReadyClips();

        const eMessage = e instanceof Error ? e.message : 'Unknown error';
        onError.current(`Failed to add clip to queue: ${eMessage}`);
      }
    },
    [drainReadyClips],
  );

  const stopAll = useCallback(() => {
    generation.current += 1;
    failedSequences.current.clear();
    nextExpectedSequence.current = nextSequence.current;
    isInitialized.current = false;
    setIsPlaying(false);
    setIsAudioMuted(false);

    for (const source of scheduledSources.current) {
      source.stop();
      source.disconnect();
    }

    scheduledSources.current.clear();
    nextStartTime.current = 0;

    if (gainNode.current) {
      gainNode.current.disconnect();
      gainNode.current = null;
    }

    const context = audioContext.current;
    audioContext.current = null;
    void context?.close().catch(() => {});

    pendingClips.current = [];
    setQueueLength(0);
  }, []);

  /**
   * Barge-in. Stops everything already handed to the audio clock, not just the
   * audible clip — `stop()` on a source scheduled in the future cancels it.
   *
   * Resetting `nextStartTime` is what makes the agent's next reply start
   * immediately: leaving the cursor where the discarded speech would have
   * ended would delay it by exactly the audio the caller talked over.
   */
  const clearQueue = useCallback(() => {
    generation.current += 1;
    failedSequences.current.clear();
    for (const source of scheduledSources.current) {
      source.stop();
      source.disconnect();
    }

    scheduledSources.current.clear();
    nextStartTime.current = 0;

    pendingClips.current = [];
    nextExpectedSequence.current = nextSequence.current;
    setQueueLength(0);
    setIsPlaying(false);
  }, []);

  const muteAudio = useCallback(() => {
    if (gainNode.current && audioContext.current) {
      gainNode.current.gain.setValueAtTime(0, audioContext.current.currentTime);
      setIsAudioMuted(true);
    }
  }, []);

  const unmuteAudio = useCallback(() => {
    if (gainNode.current && audioContext.current) {
      gainNode.current.gain.setValueAtTime(1, audioContext.current.currentTime);
      setIsAudioMuted(false);
    }
  }, []);

  return useMemo(
    () => ({
      addToQueue,
      initPlayer,
      isPlaying,
      isAudioMuted,
      muteAudio,
      unmuteAudio,
      stopAll,
      clearQueue,
      queueLength,
    }),
    [
      addToQueue,
      initPlayer,
      isPlaying,
      isAudioMuted,
      muteAudio,
      unmuteAudio,
      stopAll,
      clearQueue,
      queueLength,
    ],
  );
};
